import { ExternalLink, FlaskConical } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { PageHeaderLeading } from '@/components/navigation/page-header-leading'
import { getPageHeader } from '@/constants'
import { DashboardStatsSkeleton } from '@/components/dashboard/dashboard-section-skeleton'
import { useObservabilityStatus } from '@/hooks/use-observability-status'

export function AgentEvaluationsPage() {
  const { data, isLoading, error, refetch, isFetching } = useObservabilityStatus()
  const langsmith = data?.langsmith
  const header = getPageHeader(
    '/developer/agent-evaluations',
    'Agent evaluations',
    'LangSmith connection status and evaluation observability.',
  )

  const connected = langsmith?.connected === true

  return (
    <PageContainer>
      <PageHeader
        leading={<PageHeaderLeading />}
        title={header.title}
        description={header.description}
        icon={<FlaskConical className="h-5 w-5" />}
      />

      {isLoading ? (
        <DashboardStatsSkeleton count={3} maxColumns={3} />
      ) : error ? (
        <div className="flex flex-col items-center gap-4 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-6 text-center text-sm">
          <div>
            <p className="font-medium text-destructive">Could not load observability status</p>
            <p className="mt-1 text-muted-foreground">
              {(error as Error).message ?? 'Something went wrong.'}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            Retry
          </Button>
        </div>
      ) : (
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <CardTitle>LangSmith</CardTitle>
                <CardDescription>Tracing connection for agent runs and evaluations</CardDescription>
              </div>
              <Badge variant={connected ? 'successSoft' : 'warningSoft'}>
                {connected ? 'Connected' : 'Not connected'}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <dl className="grid grid-cols-1 gap-4 text-sm @2col:grid-cols-3">
              <div>
                <dt className="text-muted-foreground">Project</dt>
                <dd className="font-medium">{langsmith?.project?.trim() || '—'}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Trace count</dt>
                <dd className="font-medium">
                  {typeof langsmith?.trace_count === 'number'
                    ? langsmith.trace_count.toLocaleString()
                    : '—'}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Run count</dt>
                <dd className="font-medium">
                  {typeof langsmith?.run_count === 'number'
                    ? langsmith.run_count.toLocaleString()
                    : '—'}
                </dd>
              </div>
            </dl>
            <div className="flex flex-wrap gap-2">
              {langsmith?.external_url ? (
                <Button variant="outline" size="sm" asChild>
                  <a href={langsmith.external_url} target="_blank" rel="noopener noreferrer">
                    Open LangSmith
                    <ExternalLink className="size-4" aria-hidden />
                  </a>
                </Button>
              ) : null}
              <Button
                variant="ghostMuted"
                size="sm"
                onClick={() => void refetch()}
                disabled={isFetching}
              >
                Refresh
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Coming soon</CardTitle>
          <CardDescription>Evaluation workflows and regression suites</CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li>• Dataset-backed evaluation runs against production rubrics</li>
            <li>• Side-by-side trace comparison for prompt changes</li>
            <li>• Automated regression alerts on score drift</li>
          </ul>
        </CardContent>
      </Card>
    </PageContainer>
  )
}
